import React from "react";

import "../../../layout/leaflet.scss";
import MapIcon from "../../../../assets/image/marker-icon.png";

const LEAFLET_SRC = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
const LEAFLET_INTEGRITY = "sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=";

/* ⚠ THE BASEMAP IS AN UNRESOLVED LICENSING QUESTION, AND THIS IS NOT THE SAFE ANSWER — it is the
   status quo. `tile.openstreetmap.org` is what the app has always used, but the OSMF Tile Usage
   Policy asks that it NOT be used for commercial or heavy traffic, so this is a known debt, not a
   cleared one. See docs/design/spec/02-components.md#maps.

   CARTO Positron was tried here and reverted on 2026-08-24: its basemaps are free with attribution
   for light use, but the terms expect an account for commercial volume, and because there is no
   API key it works silently until it is rate-limited — the worst failure mode for production. That
   is a paid dependency wearing a free coat, so it is not an improvement over the debt above.

   The genuinely production-safe routes are vector and both need MapLibre GL rather than raster
   tiles: OpenFreeMap (no key, self-hostable) or self-hosted Protomaps. Either is a real change —
   a new CDN dependency and a different layer type — and a commercial decision, so it is deliberately
   NOT made here. Do not swap this URL for another free raster host without checking its terms:
   every well-known one carries the same restriction. */
const TILE_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const TILE_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

let leafletPromise = null;

function ensureLeaflet() {
  if (window.L) return Promise.resolve(window.L);
  if (leafletPromise) return leafletPromise;

  const pending = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = LEAFLET_SRC;
    script.integrity = LEAFLET_INTEGRITY;
    script.crossOrigin = "";
    script.async = true;
    script.onload = () => resolve(window.L);
    script.onerror = err => reject(err);
    document.body.appendChild(script);
  });

  /* ⚠ A REJECTION MUST NOT STAY MEMOIZED. One CDN blip used to poison this module for the whole
     session — every later mount got the same failed promise back and no map could ever load
     again. Clearing it lets the next mount retry; the guard keeps a slow failure from clobbering
     a newer, successful attempt. */
  pending.catch(() => {
    if (leafletPromise === pending) leafletPromise = null;
  });

  leafletPromise = pending;
  return pending;
}

/* One content signature per collection. Identity comparison is not enough: every poll builds a
   new array and every unrelated re-render reaches componentDidUpdate, so comparing references
   would rebuild every pin on each pass — a visible flicker. Signing the CONTENT means the caller
   needs no memoization discipline at all. */
const markerSig = markers => (markers || []).map(m => [m.key, m.lat, m.lng, m.tone, m.kind, m.label, m.photo].join(":")).join("|");

const lineSig = (track, plan) => JSON.stringify([track || null, plan || null]);

const isNum = value => Number.isFinite(value);

export default class LeafletMap extends React.Component {
  constructor(props) {
    super(props);
    this.containerId = `pt-map-${Math.random()
      .toString(36)
      .slice(2)}`;
    this.map = null;
    this.marker = null;
    this._destroyed = false;
    this._fitted = false;
    this.state = { failed: false };
  }

  componentDidMount() {
    ensureLeaflet().then(
      L => {
        if (!this._destroyed) this.init(L);
      },
      () => {
        /* Graceful fallback rather than a blank box — spec/02 asks for coordinates plus a
           notice when the CDN is unreachable. */
        if (!this._destroyed) this.setState({ failed: true });
      }
    );
  }

  init(L) {
    const { lat, lng, zoom = 13, editable, markers } = this.props;
    const center = [lat ?? 0, lng ?? 0];

    this.L = L;
    /* `scrollWheelZoom: false` per spec/02 — a map that eats the wheel makes the long form it
       sits in unusable. Zoom via the +/- controls.
       ponytail: no ctrl+wheel escape hatch. Leaflet has no native modifier option; if precise
       zooming is missed in the point editor, a keydown listener toggling
       `map.scrollWheelZoom.enable()` is the upgrade. */
    this.map = L.map(this.containerId, { scrollWheelZoom: false }).setView(center, zoom);

    L.tileLayer(TILE_URL, { attribution: TILE_ATTR, maxZoom: 19 }).addTo(this.map);

    /* The single draggable pin, for the coordinate editor and the visit's point.
       ⚠ Gated on `markers` being ABSENT — never on lat/lng being present. LocationPickerField
       opens a NEW point with no coordinates at all and depends on this pin existing at [0,0]:
       both the drag handler and the map-click handler call `this.marker.setLatLng`, so gating on
       a coordinate would make creating a point impossible. */
    if (!markers) {
      const icon = L.icon({ iconUrl: MapIcon, iconSize: [25, 41], iconAnchor: [12, 41] });
      this.marker = L.marker(center, { icon, draggable: !!editable }).addTo(this.map);

      if (editable) {
        this.marker.on("dragend", () => {
          const { lat: newLat, lng: newLng } = this.marker.getLatLng();
          if (this.props.onChange) this.props.onChange({ lat: newLat, lng: newLng });
        });

        this.map.on("click", e => {
          this.marker.setLatLng(e.latlng);
          if (this.props.onChange) this.props.onChange({ lat: e.latlng.lat, lng: e.latlng.lng });
        });
      }
    }

    this.lines = L.layerGroup().addTo(this.map);
    this.pins = L.layerGroup().addTo(this.map);

    this.sync(true);

    setTimeout(() => this.map && this.map.invalidateSize(), 0);
  }

  /* A divIcon rather than an image: Leaflet's default icon resolves its PNGs relative to its own
     CSS, which breaks under a bundler. `html` is an ELEMENT, not a string — Leaflet 1.9 accepts
     one, and setting textContent sidesteps escaping a user-supplied name. Passing `className`
     replaces Leaflet's own `leaflet-div-icon`, so the default white box never appears. */
  pin(marker) {
    const el = document.createElement("span");
    el.className = ["lf-pin", marker.tone && `lf-pin--${marker.tone}`, marker.kind && `lf-pin--${marker.kind}`].filter(Boolean).join(" ");

    const label = marker.label == null ? "" : String(marker.label);

    if (marker.photo) {
      /* ⚠ ALWAYS give an avatar an onError fallback. Old records point at uploads whose files are
         long gone, so without this the pin renders as a broken image — and unlike antd's <Avatar>,
         a raw <img> has no children to fall back to. On failure the img is dropped and the
         initials take over, which is the house avatar anyway. */
      const img = document.createElement("img");
      img.src = marker.photo;
      img.alt = "";
      img.onerror = () => {
        img.remove();
        el.textContent = label;
      };
      el.appendChild(img);
    } else {
      el.textContent = label;
    }

    /* ⚠ Must MATCH the CSS width for that kind, or `iconAnchor` centres the wrong box and the pin
       sits visibly off its own coordinate. lf-pin--here is 42, lf-pin--who is 34, lf-pin--dot is
       12 (a GPS ping, not a place), the rest 28. */
    const size = marker.kind === "here" ? 42 : marker.kind === "who" ? 34 : marker.kind === "dot" ? 12 : 28;
    return this.L.divIcon({ html: el, className: "lf-pin-host", iconSize: [size, size], iconAnchor: [size / 2, size / 2] });
  }

  drawPins() {
    const { markers } = this.props;
    this.pins.clearLayers();
    (markers || [])
      .filter(m => isNum(m.lat) && isNum(m.lng))
      .forEach(m => {
        const pin = this.L.marker([m.lat, m.lng], {
          icon: this.pin(m),
          title: m.title || undefined,
          riseOnHover: true,
          /* Makes the pin FOCUSABLE — Leaflet sets `tabindex="0"` and `role="button"` on the icon.
             It does not make it operable; see the keydown handler below. */
          keyboard: !!m.clickable,
        }).addTo(this.pins);

        if (m.clickable) {
          /* `this.props` is read at CALL time, so a marker built in an earlier sync never holds a
             stale handler. */
          pin.on("click", () => this.props.onMarkerClick && this.props.onMarkerClick(m));

          /* ⚠ LEAFLET'S `keyboard: true` IMPLEMENTS NO ACTIVATION. It sets `tabindex` and
             `role="button"` and stops there — and a browser only synthesises click-on-Enter for a
             real <button> or <a>, never for a `role="button"` element. So the pin was focusable and
             completely dead to the keyboard, which is the `<div onClick>` failure spec/03 forbids,
             wearing an ARIA role that promised otherwise. Verified by dispatching Enter: nothing
             happened, on `keydown` and on Leaflet's `keypress` alike.
             Space is handled too, because `role="button"` advertises both. */
          const el = pin.getElement();
          if (el) {
            el.addEventListener("keydown", event => {
              if (event.key !== "Enter" && event.key !== " ") return;
              event.preventDefault();
              if (this.props.onMarkerClick) this.props.onMarkerClick(m);
            });
          }
        }
      });
  }

  drawLines() {
    const { track, plan } = this.props;
    this.lines.clearLayers();
    /* Leaflet renders polylines as real SVG in the DOM, so the stroke comes from a token via a
       class — no hex passed through Leaflet's options, and the lines follow the theme. */
    if (Array.isArray(plan) && plan.length >= 2) {
      this.L.polyline(plan, { className: "lf-plan" }).addTo(this.lines);
    }
    if (Array.isArray(track) && track.length >= 2) {
      this.L.polyline(track, { className: "lf-track" }).addTo(this.lines);
    }
  }

  /* Every coordinate the DATA props contribute. Deliberately excludes the primary lat/lng marker:
     fitting to it would override the caller's `zoom`, and LocationPickerField opens a new point at
     zoom 6 on purpose. No data props => no fit => the constructor's setView stands. */
  dataPoints() {
    const { markers, track, plan } = this.props;
    const out = [];
    (markers || []).forEach(m => {
      if (isNum(m.lat) && isNum(m.lng)) out.push([m.lat, m.lng]);
    });
    if (Array.isArray(track)) track.forEach(p => out.push(p));
    if (Array.isArray(plan)) plan.forEach(p => out.push(p));
    return out;
  }

  fit() {
    const points = this.dataPoints();
    if (!points.length) return;
    this._fitted = true;
    /* One point has no bounds to speak of — fitBounds would slam to max zoom. Centre instead, so
       a lone active merchandiser is visible rather than a map parked in the Atlantic. */
    if (points.length === 1) {
      this.map.setView(points[0], this.props.zoom ?? 13);
      return;
    }
    this.map.fitBounds(this.L.latLngBounds(points), { padding: [24, 24], maxZoom: 16 });
  }

  sync(force) {
    if (!this.map) return;
    const { markers, track, plan, fitKey } = this.props;

    const nextMarkers = markerSig(markers);
    if (force || nextMarkers !== this._markerSig) {
      this._markerSig = nextMarkers;
      this.drawPins();
    }

    const nextLines = lineSig(track, plan);
    if (force || nextLines !== this._lineSig) {
      this._lineSig = nextLines;
      this.drawLines();
    }

    /* Fit is driven by `fitKey` ALONE, and re-armed rather than fired directly: comparing
       prevProps.fitKey to fitKey would miss first-data-arrival, since the day and the selection
       are already set at mount and the rows land later. Arming a flag and fitting on the first
       sync that actually has bounds covers both. A poll changes no key, so it never touches the
       pan or zoom the user set. */
    if (!force && fitKey !== this._fitKey) this._fitted = false;
    this._fitKey = fitKey;
    if (!this._fitted) this.fit();
  }

  componentDidUpdate(prevProps) {
    if (!this.map) return;

    this.sync(false);

    /* The single-marker path. Untouched behaviour for the two callers that use it: move the pin
       in place, never rebuild the map (spec/02) and never change the zoom. */
    if (!this.marker) return;
    const { lat, lng } = this.props;
    if (!isNum(lat) || !isNum(lng)) return;
    if (prevProps.lat === lat && prevProps.lng === lng) return;

    this.marker.setLatLng([lat, lng]);
    this.map.setView([lat, lng], this.map.getZoom());
  }

  componentWillUnmount() {
    this._destroyed = true;
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  render() {
    const { height = 320, lat, lng, failedText } = this.props;

    if (this.state.failed) {
      return (
        <div className="pt-map pt-map--failed" style={{ height, width: "100%" }}>
          <span>{failedText || "Map unavailable"}</span>
          {isNum(lat) && isNum(lng) ? <code>{`${lat.toFixed(5)}, ${lng.toFixed(5)}`}</code> : null}
        </div>
      );
    }

    return <div id={this.containerId} className="pt-map" style={{ height, width: "100%" }} />;
  }
}
